import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { createSelector } from 'reselect';

import { usersActions } from '../store';

import { FormControl, InputAdornment, InputLabel, MenuItem, Paper, Popover, Select } from '@material-ui/core';
import Toolbar from '@material-ui/core/Toolbar';
import IconButton from '@material-ui/core/IconButton';
import ListIcon from '@material-ui/icons/List';
import MapIcon from '@material-ui/icons/Map';
import SearchIcon from '@material-ui/icons/Search';
import InputBase from '@material-ui/core/InputBase';
import Badge from '@material-ui/core/Badge';
import { Tune } from '@material-ui/icons';
import Tooltip from '@material-ui/core/Tooltip';
import Fab from '@material-ui/core/Fab';
import AddIcon from '@material-ui/icons/Add';
import { makeStyles } from '@material-ui/core/styles';

import { t } from '../common/LocalizationProvider';
import {cyberStyles} from "../cssShared/cyberTheme";

const getDevicesItems = (state) => state.devices.items;

export const getAllDevicesArray = createSelector(
    [getDevicesItems],
    (devicesItems) => Object.values(devicesItems)
);

export const getStatusCounts = createSelector(
    [getAllDevicesArray],
    (allDevices) => {
        return allDevices.reduce((acc, device) => {
            acc[device.status] = (acc[device.status] || 0) + 1;
            return acc;
        }, {
            online: 0,
            slowing: 0,
            offline: 0,
            stopped: 0
        });
    }
);

const useStyles = makeStyles((theme) => ({
    ...cyberStyles(theme),
    root: {
        height: '100%',
        position: 'relative',
    },
    toolbar: {
        display: 'flex',
        gap: theme.spacing(2),
        justifyContent: 'space-between',
        padding: theme.spacing(0, 2),
    },
}));

const useDelay = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => { setDebouncedValue(value); }, delay);
        return () => { clearTimeout(handler); };
    }, [value, delay]);
    return debouncedValue;
};

const SearshDevices = ({ setDeviceList, deviceList, setDevicesFiltred, setSortBy, sortBy }) => {
    const classes = useStyles();
    const history = useHistory();

    const dispatch = useDispatch();
    const allDevices = useSelector(getAllDevicesArray);
    const statusCounts = useSelector(getStatusCounts);
    const users = useSelector(state => state.users.items);

    const [deviceName, setDeviceName] = useState('');
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const [filter, setFilter] = useState({ statuses: [], groupIds: [] });

    const inputRef = useRef();
    const delayedDeviceName = useDelay(deviceName, 300);

    const handleChange = useCallback((event) => {
        setDeviceName(event.target.value);
    }, []);

    const handleAdd = useCallback(() => {
        history.push('/device');
    }, [history]);

    const handleOpenFilter = useCallback(() => {
        setFilterAnchorEl(inputRef.current);

        const fetchUsersData = async () => {
            try {
                const response = await fetch('/api/users');
                if (response.ok) {
                    const res = await response.json();
                    dispatch(usersActions.setItems(res));
                } else {
                    console.error('Failed to fetch users');
                }
            } catch (e) {
                console.error('Error fetching users:', e);
            }
        };

        if (users.length === 0) {
            fetchUsersData();
        }
    }, [users.length]);

    useEffect(() => {
        let isActive = true;
        const executeFilter = async () => {
            const searchRegex = new RegExp(delayedDeviceName.toLowerCase().split('').join('.*'), 'i');
            let currentFiltered = allDevices.filter(item => {
                const nameMatch = searchRegex.test(item.name.toLowerCase());
                const idMatch = item.uniqueId.toLowerCase().startsWith(delayedDeviceName.toLowerCase());
                const statusMatch = filter.statuses.length === 0 || filter.statuses.includes(item.status);
                return (nameMatch || idMatch) && statusMatch;
            });

            if (filter.groupIds.length > 0) {
                try {
                    const fetchPromises = filter.groupIds.map(userId =>
                        fetch(`/api/devices?userId=${userId}`).then(res => res.ok ? res.json() : null)
                    );
                    const results = await Promise.all(fetchPromises);
                    const groupDeviceIds = new Set();
                    results.forEach(res => {
                        if (res) {
                            res.forEach(d => groupDeviceIds.add(d.id));
                        }
                    });
                    currentFiltered = currentFiltered.filter(d => groupDeviceIds.has(d.id));
                } catch (e) {
                    console.error('Error filtering by group:', e);
                }
            }

            if (isActive) {
                setDevicesFiltred(currentFiltered);
            }
        };

        executeFilter();

        return () => {
            isActive = false;
        };
    }, [allDevices, delayedDeviceName, filter.statuses, filter.groupIds, setDevicesFiltred]);

    return (
        <Paper square elevation={0} className={classes.header}>
            <Toolbar className={classes.toolbar} disableGutters>
                <div style={{ display: 'flex', alignItems: 'center', flex: 1, gap: '8px' }}>
                    <Tooltip title={deviceList ? t('HideDevicesList') : t('ShowDevicesList')}>
                        <IconButton
                        className={classes.toggleButton}
                        onClick={() => setDeviceList((prevState) => !prevState)}
                        size="medium"
                    >
                        {!deviceList ? <ListIcon /> : <MapIcon />}
                    </IconButton>
                    </Tooltip>

                    <div className={classes.search}>
                        <div className={classes.searchIcon}>
                            <SearchIcon />
                        </div>
                        <InputBase
                            ref={inputRef}
                            placeholder={t('selectDevice')}
                            classes={{ root: classes.inputRoot, input: classes.inputInput }}
                            onChange={handleChange}
                            value={deviceName}
                            endAdornment={(
                                <InputAdornment position="start">
                                    <IconButton size="small" onClick={handleOpenFilter} style={{color: '#00f2ff'}}>
                                        <Badge variant="dot" color="error" invisible={filter.statuses.length === 0 && filter.groupIds.length === 0}>
                                            <Tune fontSize="small" />
                                        </Badge>
                                    </IconButton>
                                </InputAdornment>
                            )}
                        />
                        <Popover
                            open={!!filterAnchorEl}
                            anchorEl={filterAnchorEl}
                            onClose={() => setFilterAnchorEl(null)}
                            classes={{ paper: classes.popoverPaper }}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                        >
                            <div className={classes.filterPanel}>
                                <FormControl variant="outlined" className={classes.formControl}>
                                    <InputLabel>{t('deviceStatus')}</InputLabel>
                                    <Select
                                        label={t('deviceStatus')}
                                        value={filter.statuses}
                                        onChange={(e) => setFilter((prev) => ({ ...prev, statuses: e.target.value }))}
                                        multiple
                                        MenuProps={{PaperProps: { className: classes.periodMenuPaper },}}
                                    >
                                        <MenuItem value='online' className={classes.menuItem}>{t('eventDeviceOnline')} ({statusCounts.online})</MenuItem>
                                        <MenuItem value='slowing' className={classes.menuItem}>{t('eventDeviceSlowing')} ({statusCounts.slowing})</MenuItem>
                                        <MenuItem value='offline' className={classes.menuItem}>{t('eventDeviceOffline')} ({statusCounts.offline})</MenuItem>
                                        <MenuItem value='stopped' className={classes.menuItem}>{t('eventDeviceStopped')} ({statusCounts.stopped})</MenuItem>
                                    </Select>
                                </FormControl>
                                <FormControl variant="outlined" className={classes.formControl}>
                                    <InputLabel>{t('settingsGroups')}</InputLabel>
                                    <Select
                                        label={t('settingsGroups')}
                                        value={filter.groupIds}
                                        onChange={(e) => setFilter((prev) => ({ ...prev, groupIds: e.target.value }))}
                                        multiple
                                        MenuProps={{PaperProps: { className: classes.periodMenuPaper },}}
                                    >
                                        {users.map((object) => (
                                            <MenuItem key={object.user.id} value={object.user.id} className={classes.menuItem}>{object.user.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl variant="outlined" className={classes.formControl}>
                                    <InputLabel>{t('SortBy')}</InputLabel>
                                    <Select
                                        label={t('sharedName')}
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                        MenuProps={{PaperProps: { className: classes.periodMenuPaper },}}
                                    >
                                        <MenuItem value='name' className={classes.menuItem}>{t('sharedName')}</MenuItem>
                                        <MenuItem value='updated' className={classes.menuItem}>{t('deviceLastUpdate')}</MenuItem>
                                        <MenuItem value='createdAt' className={classes.menuItem}>{t('firstCreated')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </div>
                        </Popover>
                    </div>

                    <Tooltip title={t('addNewDevice')}>
                        <Fab size="small" className={classes.plusIcon} onClick={handleAdd}>
                            <AddIcon />
                        </Fab>
                    </Tooltip>
                </div>
            </Toolbar>
        </Paper>
    );
};

export default React.memo(SearshDevices);